import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import { api, media } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { cart, clearCart, user, addToCart } = useApp();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState({ name: user?.name || "", email: user?.email || "", address: "", city: "", country: "", zip: "" });
  const [placing, setPlacing] = useState(false);
  const [addons, setAddons] = useState([]);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("");

  useEffect(() => {
    api.get("/invoice/payment-methods").then(({ data }) => { setMethods(data); if (data[0]) setMethod(data[0].key); }).catch(() => {});
    let cancelled = false;
    (async () => {
      try {
        const { data: cs } = await api.get("/products", { params: { product_type: "case" } });
        const withV = await Promise.all(cs.map(async (p) => {
          try { const { data: vs } = await api.get(`/variants/by-product/${p.slug}`); return { product: p, variant: vs[0] }; }
          catch { return { product: p, variant: null }; }
        }));
        if (!cancelled) setAddons(withV.filter((a) => a.variant));
      } catch { if (!cancelled) setAddons([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasVials = cart.some((x) => x.product?.product_type !== "case");
  const suggestions = addons.filter((a) => !cart.some((x) => x.product?.slug === a.product.slug));

  const subtotal = cart.reduce((a, x) => a + x.variant.price * x.qty, 0);
  const qty = cart.reduce((a, x) => a + x.qty, 0);
  const tier = qty >= 50 ? 30 : qty >= 25 ? 22 : qty >= 10 ? 15 : qty >= 5 ? 10 : 0;
  const discount = subtotal * tier / 100;
  const total = subtotal - discount;

  const place = async () => {
    if (!cart.length) return toast.error("Cart is empty");
    if (!shipping.email) return toast.error("Email required");
    setPlacing(true);
    try {
      const { data } = await api.post("/invoice/checkout", {
        items: cart.map((x) => ({ variant_id: x.variant.id, quantity: x.qty })),
        email: shipping.email,
        payment_method: method,
        shipping,
        ref_code: localStorage.getItem("cgl_ref") || undefined,
        origin: window.location.origin,
      });
      if (data.invoice_number) { clearCart(); navigate(`/invoice/${data.invoice_number}`); return; }
      toast.error("Could not create invoice");
      setPlacing(false);
    } catch { toast.error("Checkout failed"); setPlacing(false); }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-5xl mx-auto">
      <div className="mono-label mb-3">Checkout</div>
      <h1 className="font-serif text-5xl text-white mb-10">Complete Your Order</h1>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6 space-y-4">
            <div className="mono-label">Shipping</div>
            {[["name","Full name"],["email","Email"],["address","Address"],["city","City"],["country","Country"],["zip","ZIP / Postal"]].map(([k, ph]) => (
              <Input key={k} value={shipping[k]} onChange={(e) => setShipping({ ...shipping, [k]: e.target.value })} placeholder={ph}
                data-testid={`checkout-${k}`} className="bg-black/40 border-white/10" />
            ))}
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="mono-label mb-3">Payment method</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {methods.map((m) => (
                <button key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`checkout-method-${m.key}`}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${method === m.key ? "bg-cyan-400 text-black border-cyan-400" : "border-white/15 text-slate-200 hover:border-cyan-400/50"}`}>{m.label}</button>
              ))}
            </div>
            <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-xs font-mono text-slate-400 leading-relaxed">
              We'll generate an <span className="text-cyan-300">invoice</span> with payment instructions. Pay via PayPal, Venmo, Zelle, Google Pay or Apple Pay, then confirm — your order ships once payment is verified.
            </div>
          </div>

          {hasVials && suggestions.length > 0 && (
            <div className="glass rounded-2xl p-6" data-testid="checkout-addons">
              <div className="mono-label mb-1">Complete your kit</div>
              <p className="text-xs text-slate-400 mb-4">Keep your peptides safe & organized — add a pharmacist-designed storage case.</p>
              <div className="space-y-3">
                {suggestions.map(({ product, variant }) => (
                  <div key={product.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`addon-${product.slug}`}>
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/40 shrink-0 flex items-center justify-center">
                      {product.image && <img src={media(product.image)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{product.name.replace("CG Labz ", "")}</div>
                      <div className="text-[11px] font-mono text-slate-400 mt-0.5">
                        {product.vial_capacity != null ? `${product.vial_capacity} vials · ` : ""}${variant.price.toFixed(2)}
                        {variant.compare_at_price > variant.price && <span className="text-slate-600 line-through ml-1.5">${variant.compare_at_price.toFixed(2)}</span>}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => { addToCart(variant, product, 1); toast.success(`${product.name.replace("CG Labz ", "")} added`); }}
                      data-testid={`addon-add-${product.slug}`} className="btn-ghost rounded-lg text-xs shrink-0">Add</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="glass-strong rounded-2xl p-6 space-y-3" data-testid="checkout-summary">
            <div className="mono-label mb-2">Order summary</div>
            {cart.map((x) => (
              <div key={x.variant.id} className="flex justify-between text-sm font-mono border-b border-white/5 pb-2">
                <span className="text-slate-300">{x.product.name} · {x.variant.label || `${x.variant.mg}${x.variant.unit}`} × {x.qty}</span>
                <span>${(x.variant.price * x.qty).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span></div>
            {tier > 0 && <div className="flex justify-between text-sm text-emerald-400"><span>Bulk discount ({tier}%)</span><span className="font-mono">−${discount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-sm"><span className="text-slate-400">Shipping</span><span className="font-mono text-slate-500">Calculated at fulfillment</span></div>
            <div className="laser-line" />
            <div className="flex justify-between text-lg"><span>Total</span><span className="font-mono text-cyan-300">${total.toFixed(2)}</span></div>
            <Button onClick={place} disabled={placing || !cart.length} data-testid="place-order-btn" className="w-full mt-2 btn-primary rounded-xl py-3 text-sm tracking-wide">
              {placing ? "Creating invoice…" : "Place order & get invoice"}
            </Button>
            <p className="text-[10px] font-mono text-slate-600 text-center">For laboratory research only. Not for human consumption.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
